/**
 * Ogohlantirish bloki.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ OGOHLANTIRISH PULGA TEGISHLI — DEMAK U BEZAK EMAS                       ║
 * ║                                                                          ║
 * ║ Bu ilovada `warn` va `danger` ko'pincha shunday gaplarni aytadi:         ║
 * ║ «davr yopilgan», «qoldiq manfiy», «kurs eskirgan». Ular e'tibordan       ║
 * ║ chetda qolsa natija — noto'g'ri provodka va qo'lda tuzatiladigan hisob.  ║
 * ║                                                                          ║
 * ║ Ilgari to'rt tonning farqi FAQAT fon rangida edi. Rangni ajrata olmagan  ║
 * ║ (erkaklarning ~8%) yoki ekranga yondan qaragan foydalanuvchi uchun       ║
 * ║ «hammasi joyida» va «pulingiz noto'g'ri hisoblanadi» bir xil ko'rinardi. ║
 * ║                                                                          ║
 * ║ Endi har tonning O'Z BELGISI bor (uchburchak, xoch, belgi, nishon) —     ║
 * ║ ma'no shakl orqali ham beriladi, faqat rang orqali emas (WCAG §1.4.1).   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { t } from '../../lib/i18n';

export type AlertTone = 'info' | 'warn' | 'danger' | 'ok';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  readonly tone?: AlertTone;
  /** Qalin birinchi qator — muammoning NOMI. Tafsilot `children` da. */
  readonly title?: ReactNode;
  /**
   * Berilsa — o'ng chetda yopish tugmasi chiqadi.
   *
   * ⚠️ Faqat FOYDALANUVCHI ko'rgan va tushungan xabar uchun (masalan
   * «saqlandi»). Blokirovka qiluvchi xato yopilmasligi kerak: yopilgan
   * xato — unutilgan xato.
   */
  readonly onDismiss?: () => void;
  /** Yopish tugmasining skrinrider matni. Berilmasa — «Yopish». */
  readonly dismissLabel?: string;
}

/*
 * Chegara ATAYLAB qalinroq (`border-2`) va rangi to'yingan. 1px yumshoq
 * chegara karta chegarasidan farq qilmasdi va blok sahifaning oddiy bir
 * qismidek ko'rinardi.
 */
const TONES: Record<AlertTone, string> = {
  info: 'bg-brand-soft text-ink border-brand/40',
  warn: 'bg-warn-soft text-ink border-warn/60',
  danger: 'bg-danger-soft text-ink border-danger/60',
  ok: 'bg-ok-soft text-ink border-ok/50',
};

/** Belgi rangi — matn emas, aynan belgi bo'yaladi: yozuv `text-ink` bo'lib qoladi. */
const ICON_TONES: Record<AlertTone, string> = {
  info: 'text-brand',
  warn: 'text-warn',
  danger: 'text-danger',
  ok: 'text-ok',
};

/*
 * Belgilar QO'LDA chizilgan `path` lar.
 *
 * Ikonka kutubxonasi qo'shish rad etildi: bu to'rtta shakl uchun ~50 KB
 * bog'liqlik va yana bitta yangilanadigan paket degani (MVP_PLAN §6).
 */
const ICON_PATHS: Record<AlertTone, ReactNode> = {
  // Doira ichida «i» — xabar.
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 7.6v.2" />
    </>
  ),
  // Uchburchak — «ehtiyot bo'l», yo'l belgisi bilan bir xil ma'no.
  warn: (
    <>
      <path d="M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 16.4v.2" />
    </>
  ),
  // Doira ichida xoch — «to'xta».
  danger: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </>
  ),
  // Doira ichida belgi — «bajarildi».
  ok: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </>
  ),
};

export function Alert({
  tone = 'info',
  title,
  onDismiss,
  dismissLabel,
  className,
  children,
  ...props
}: AlertProps): ReactNode {
  return (
    <div
      /*
       * Xato xabari skrinriderga darhol o'qilishi kerak — login ekranida muhim.
       *
       * `warn` ATAYLAB `alert` emas: davr yopilishi ekranida bir vaqtda
       * o'nlab ogohlantirish chiqadi va ularning hammasi «assertive»
       * bo'lsa, skrinrider foydalanuvchi o'qiyotgan jumlani o'nta marta
       * bo'lib tashlardi. `status` (polite) navbat bilan o'qiydi.
       */
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-2.5 rounded-md border-2 px-3 py-2.5 text-sm', TONES[tone], className)}
      {...props}
    >
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        // `mt-0.5` — belgi birinchi qator matnining o'rtasiga tushadi, tepasiga emas.
        className={cn('mt-0.5 size-[1.15em] shrink-0', ICON_TONES[tone])}
      >
        {ICON_PATHS[tone]}
      </svg>
      {/* `min-w-0` — uzun xato matni (masalan server javobi) blokni cho'zmasin, o'ralsin. */}
      <div className="min-w-0 flex-1">
        {title !== undefined && title !== null && title !== false && (
          <p className="font-semibold">{title}</p>
        )}
        {children}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel ?? t('common.close')}
          /*
           * 28px nishon — matn yonidagi kichik tugma uchun eng kichik
           * qulay o'lcham. Belgi 14px, qolgani bosish maydoni: belgi
           * o'lchamidagi tugma sichqoncha bilan aniq bosilmasdi.
           */
          className={cn(
            'ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md',
            'text-ink-muted transition-colors hover:bg-black/5 hover:text-ink dark:hover:bg-white/10',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
          )}
        >
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="size-3.5"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}
